import { handleResponse } from './'
import { objectToFormData } from 'object-to-formdata'

export const FORM_ERROR = 'FINAL_FORM/form-error'

let IS_LEGACY = false

export function setEnvironmentVars(isLegacy = false) {
	// Set in layout
	IS_LEGACY = isLegacy
}

const handleFormErrors = (json) => {
	// Process any errors.
	if (!json.success) {
		if (json.error !== undefined) {
			return {
				[FORM_ERROR]: json.error,
			}
		}
		if (json.errors !== undefined) {
			return {
				...json.errors,
			}
		}
	}

	return {
		[FORM_ERROR]:
			'We’re sorry, there was a technical error on our end that blocked you from submitting a request to reset your password. Please try again or email us at <a href="mailto:community@business.com?subject=unhandled-error">community@business.com</a> if you continue to have trouble.',
	}
}

/**
 * Generic Form sumbit and validate.
 * @todo: add JS validation, Use JSON post body, using formData as a work around for now
 *
 * @param	{Object} url Request url
 * @param	{Object} options Same options like fetch request https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
 * @param	{Object} values Form values to POST
 * @return	{Object} The shape will match the shape of the form's values
 *					 To properly trigger validation messages and styles
 */
export const onSubmit = (url, options) => (values = {}) => (handler) => {
	let formData = objectToFormData(values)

	const defaulOptions = {
		headers: {
			'X-Requested-With': 'XMLHttpRequest',
		},
		credentials: 'include',
		method: 'POST',
		body: formData,
	}

	return import(/* webpackChunkName: "merge" */ 'lodash.merge')
		.then(({ default: merge }) =>
			fetch(url, merge(defaulOptions, options)).then(handleResponse).then(handler).catch(handleFormErrors)
		)
		.catch(handleFormErrors)
}

export const checkRedirectAndCloseModal = (modalVisible, setModalVisible, redirect) => {
	if (IS_LEGACY || !modalVisible) {
		if (redirect !== null) {
			document.location.href = redirect
		}
	}
	setModalVisible(false)
}

export const signupRedirect = (modalVisible, setModalVisible, redirect) => {
	if (modalVisible) {
		setModalVisible(false)
	}
	document.location.href = redirect
}

/**
 * Get a list of errors with order
 * @param {Object} submitErrors object with error messages
 * @param {Object} keys array of allowed keys on errors
 */
export const getErrorMessages = (submitErrors = {}, keys = []) => {
	const messages = []
	Object.keys(submitErrors).forEach((key) => {
		let errorMessage = ''
		if (keys.indexOf(key) === -1 && key !== FORM_ERROR) {
			return
		}
		switch (key) {
			case 'password':
				submitErrors[key]['log'].forEach((message) => message && messages.push({ order: 5, message }))
				errorMessage = false
				break
			case 'firstname':
				errorMessage = { order: 1, message: submitErrors[key] }
				break
			case 'lastname':
				errorMessage = { order: 2, message: submitErrors[key] }
				break
			case 'email':
				errorMessage = { order: 3, message: submitErrors[key] }
				break
			case 'country':
				errorMessage = { order: 4, message: submitErrors[key] }
				break
			case FORM_ERROR:
				errorMessage = { order: 0, message: submitErrors[key] }
				break
			default:
				errorMessage = { order: 4, message: submitErrors[key] }
				break
		}

		errorMessage && messages.push(errorMessage)
	})

	messages.sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0))
	return messages.map((elem) => {
		return elem.message && elem.message
	})
}

/**
 * Optout or Data Request form submission
 * @param {Object} value Form values to POST
 * @param {string} value.recaptchaToken the recaptcha token to be added to the query string of the POST
 * @param {boolean} isDataRequest is this a data request (otherwise it's an optout)
 */
export function handleOptOutOrDataRequestSubmission({ recaptchaToken, ...data }, isDataRequest) {
	// switch endpoints based on isDataRequest boolean arg
	const url = `${process.env.GATSBY_RFQ_API_BASE_URL}/v1/buyer/${isDataRequest ? 'data' : 'optout'}/`

	const json = JSON.stringify(data)

	return fetch(`${url}?g-recaptcha-response=${recaptchaToken}`, {
		headers: {
			'Content-Type': 'application/json',
		},
		method: 'POST',
		body: json,
	})
		.then(handleResponse)
		.then((response) => response)
		.catch((err) => {
			console.error(err)
		})
}

/* validator functions (for use with final form forms/fields) */

/* slightly modified version of `import { required } from @duplo/core/lib/validators.js`
   https://bitbucket.org/bdcdevops/final-rfq/src/984c400f0c54c09121951096b947d06ca78cc7ac/packages/core/src/validators.js#validators.js-65:65
 */
export const required = (value) => {
	// return the right error message based on isReg flag
	const reqErrorMessage = 'Required'

	// handle dropdowns (choose below option)
	if (value === '-1') {
		return reqErrorMessage
	}

	if (typeof value === 'string' && !!value.trim().length) {
		return undefined
	}
	if (typeof value === 'number' && value > 0) {
		return undefined
	}
	if (Array.isArray(value) && value.length) {
		return undefined
	}

	return reqErrorMessage
}

/* checks if string is a valid email,
taken from https://bitbucket.org/bdcdevops/final-rfq/src/d5943db5f06628c6c206d23b07023944570ef8ac/packages/core/src/validators.js#validators.js-99:99
*/

export const isValidEmail = (value = '') =>
	/* eslint-disable-next-line */
	/^$|^([\w\!\#$\%\&\'\*\+\-\/\=\?\^\`{\|\}\~]+\.)*[\w\!\#$\%\&\'\*\+\-\/\=\?\^\`{\|\}\~]+@((((([a-zA-Z0-9]{1}[a-zA-Z0-9\-]{0,62}[a-zA-Z0-9]{1})|[a-zA-Z])\.)+[a-zA-Z]{2,})|(\d{1,3}\.){3}\d{1,3}(\:\d{1,5})?)\s*$/i.test(
		value
	)
		? undefined
		: 'Please double-check the email is entered correctly (e.g., name@domain.com).'

export const createCrossBrowserEvent = (eventName) => {
	if (!eventName) {
		console.warn('createCrossBrowserEvent was called without an eventName, please pass an eventName')
		return
	}
	// We trigger the form submit here because we have to do it when we know we have the token.
	// We need to do this check to support IE11
	let event
	if (typeof Event === 'function') {
		event = new Event(eventName, {
			cancelable: true,
		})
	} else {
		event = document.createEvent('Event')
		event.initEvent('submit', true, true)
	}
	return event
}

/**
 * this function is used to compose mutiple validator functions
 *
 * @param {...Array.function} validators - this is the array of functions passed as params
 *										to be reduced to the first error message or undefined
 *
 * @returns {function} - the function which takes a value and reduces the validator functions
 * 						passed to get a result based on the value passed
 */
export const composeValidators = (...validators) => (value) =>
	validators.reduce((error, validator) => error || validator(value), undefined)
